import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { CheckCircle2, HelpCircle } from 'lucide-react';

const serviceData = {
    'digital-marketing': {
        title: 'Digital Marketing & SEO Company in Pune',
        metaTitle: 'Digital Marketing Agency & SEO Company in Pune | PyrusMedia',
        metaDesc: 'Leading digital marketing agency & SEO company in Pune. Expert local SEO services Pune, Google Ads PPC management, and social media marketing.',
        keywords: 'digital marketing agency in Pune, SEO company Pune, SEO services Pune, local SEO services Pune, Google Ads agency Pune, social media marketing company Pune, digital agency Pune',
        summary: 'PyrusMedia is a full-service digital marketing agency and SEO company in Pune delivering data-driven ROI, higher Google rankings, and organic growth.',
        problems: ['Low Google rankings & website traffic?', 'Poor conversion rate from ad spend?', 'Lack of localized search strategy in Pune?'],
        included: [
            'Search Engine Optimization (Technical, On-page, & Off-page SEO)',
            'Local SEO & Google Business Profile Optimization in Pune ',
            'Pay-Per-Click Advertising (Google Ads & Meta Ads Management)',
            'Social Media Strategy, Content Creation & Community Management',
            'Conversion Rate Optimization (CRO) & Funnel Analytics'
        ],
        pricing: [
            {
                name: 'Starter SEO & Social',
                target: 'For local businesses & startups',
                price: '₹15,000',
                period: '/ Monthly',
                features: [
                    'Local SEO & Google Business Profile Optimization',
                    '2 Social Media Platforms Management',
                    '12–15 Engaging Posts per Month',
                    'Basic On-Page SEO & Keyword Targeting',
                    'Monthly Performance Report'
                ],
                buttonText: 'GET STARTED',
                isPopular: false
            },
            {
                name: 'Growth Digital Marketing',
                target: 'For growing medium enterprises',
                price: '₹35,000',
                period: '/ Monthly',
                features: [
                    'Comprehensive SEO (Technical + On-page + Backlinks)',
                    '3–4 Social Media Channels Managed',
                    'Google Ads & Meta Ads Campaign Setup & Optimization',
                    'Google Maps SEO & Local Citation Building',
                    '2 Keyword-Targeted Blog Posts per Month',
                    'Monthly Analytics & Strategy Review'
                ],
                buttonText: 'BOOST YOUR TRAFFIC',
                isPopular: true
            },
            {
                name: 'Enterprise SEO & Ads',
                target: 'Established Brands & E-commerce',
                price: '₹75,000',
                period: '/ Monthly',
                features: [
                    'Full Scale Technical & Content SEO Services',
                    'Multi-channel Performance Ads (Google, Meta, LinkedIn)',
                    '4 In-depth Keyword Optimized Blog Articles',
                    'Competitor Keyword Gap & Backlink Strategy',
                    'Conversion Rate Optimization & Funnel Design',
                    'Dedicated Account Manager & Weekly Strategy Calls'
                ],
                buttonText: 'TALK TO AN EXPERT',
                isPopular: false
            }
        ],
        process: ['SEO Audit & Keyword Mapping', 'Strategy & Technical Setup', 'Content & Campaign Execution', 'Continuous Optimization'],
        faqs: [
            { q: 'How does your SEO service help businesses in Pune?', a: 'We implement localized SEO strategies targeting high-intent local search queries like "digital marketing agency in Pune" and "SEO company Pune" to drive organic traffic and qualified customer leads.' },
            { q: 'What is the difference between SEO and Google Ads PPC?', a: 'Google Ads deliver immediate traffic, whereas SEO builds long-term authority and sustainable zero-cost organic traffic on Google.' }
        ]
    },
    'web-development': {
        title: 'Web Development Company in Pune',
        metaTitle: 'Web Development Company in Pune | Custom Web Apps | PyrusMedia',
        metaDesc: 'Top web development company in Pune. PyrusMedia delivers custom web development, responsive websites, SaaS platforms, and custom web application development.',
        keywords: 'web development company in Pune, custom web development, website development company Pune, responsive web development, custom web application development, custom website development',
        summary: 'PyrusMedia is a premier web development company in Pune. We craft responsive web designs, full-stack custom web applications, SaaS platforms, and enterprise websites.',
        problems: ['Slow loading speed & poor mobile performance?', 'Outdated web design causing high bounce rates?', 'Inflexible CMS unable to scale with your business?'],
        included: [
            'Custom Web Application Development (React, Next.js, Node.js)',
            'Responsive Web Development & Mobile-Friendly Architecture',
            'Custom Web Development for SaaS & Business Portals',
            'API Integration, Database Design, & High Security',
            'SEO-Friendly Code Structure & Core Web Vitals Optimization'
        ],
        process: ['Requirements & Wireframing', 'UI/UX Architecture', 'Full-Stack Development', 'QA & Launch'],
        faqs: [
            { q: 'Why choose PyrusMedia as your web development company in Pune?', a: 'We specialize in custom web application development using modern frameworks like React and Node.js to ensure lightning-fast speeds, responsive designs, and seamless scalability.' },
            { q: 'How much does custom web application development cost in India?', a: 'Costs depend on project scope and features. Contact our web development team in Pune for a tailored, transparent project quote.' }
        ]
    },
    'mobile-development': {
        title: 'Mobile App Development Company Pune',
        metaTitle: 'Mobile App Development Company Pune | iOS & Android Apps | PyrusMedia',
        metaDesc: 'Leading mobile app development company in Pune. We build custom mobile apps, native iOS/Android apps, and cross-platform Flutter/React Native solutions.',
        keywords: 'mobile app development company Pune, custom mobile app development, Android app development, iOS app development, cross platform app development, Flutter app development company',
        summary: 'As a top mobile app development company in Pune, PyrusMedia creates sleek, scalable iOS, Android, and cross-platform Flutter/React Native apps engineered for performance.',
        problems: ['Need an engaging Android & iOS app for your enterprise?', 'Poor app UX causing high user drop-offs?', 'High cross-platform development costs?'],
        included: [
            'Custom Mobile App Development for iOS & Android',
            'Cross-Platform App Development (Flutter & React Native)',
            'Backend API Engineering & Cloud Integration',
            'App Store & Play Store Publishing (ASO Optimization)',
            'App Maintenance, Security Patches, & Feature Upgrades'
        ],
        process: ['User Research & Wireframing', 'UI Design & Prototyping', 'Cross-Platform App Build', 'Store Publishing & QA'],
        faqs: [
            { q: 'What mobile app development frameworks do you specialize in?', a: 'We build high-performance mobile apps using Flutter, React Native, Swift (iOS), and Kotlin (Android) for maximum efficiency and speed.' },
            { q: 'Do you offer ongoing app maintenance and App Store Optimization?', a: 'Yes, our mobile app team in Pune provides post-launch maintenance, updates, security compliance, and ASO.' }
        ]
    },
    'software-development': {
        title: 'Software Development Company Pune',
        metaTitle: 'Software Development Company in Pune | Custom SaaS & MVP | PyrusMedia',
        metaDesc: 'Trusted software development company in Pune. PyrusMedia offers custom software development, SaaS products, MVP development, and IT solutions company services.',
        keywords: 'software development company Pune, custom software development, business software development, SaaS development, MVP development, IT solutions company Pune',
        summary: 'PyrusMedia is a leading custom software development company in Pune building robust business software, SaaS applications, and MVPs for startups and enterprises.',
        problems: ['Complex manual business operations?', 'Off-the-shelf software failing to match custom workflows?', 'Slow SaaS prototype launch timelines?'],
        included: [
            'Custom Business Software Development',
            'SaaS Product Engineering & Multi-Tenant Architecture',
            'MVP Development for Startups & Product Testing',
            'Legacy System Modernization & Cloud Migration',
            'Enterprise API Integration & Automation'
        ],
        process: ['System Architecture Design', 'Agile Software Sprint', 'Rigorous Testing & QA', 'Deployment & Scaling'],
        faqs: [
            { q: 'Why opt for custom software development vs off-the-shelf software?', a: 'Custom software gives your business tailored workflows, full ownership, zero recurring subscription fees, and complete scalability.' },
            { q: 'How long does MVP development take for startups?', a: 'Our MVP development process typically takes 4 to 8 weeks to get a launch-ready product into the market.' }
        ]
    },
    'ai-automation': {
        title: 'AI Automation Company Pune',
        metaTitle: 'AI Automation Company Pune | Custom AI & Chatbots | PyrusMedia',
        metaDesc: 'Premier AI automation company in Pune. We deliver custom AI solutions, AI workflow automation, AI chatbot development, and business process automation.',
        keywords: 'AI automation company Pune, AI automation services, AI workflow automation, AI business automation, AI chatbot development Pune, generative AI development company',
        summary: 'PyrusMedia is a custom AI automation company in Pune helping businesses automate repetitive workflows, deploy intelligent AI chatbots, and leverage generative AI.',
        problems: ['Wasting human hours on manual data entry?', 'Slow response times in customer service?', 'Inability to extract insights from raw operational data?'],
        included: [
            'AI Workflow Automation & Process Optimization',
            'Custom AI Chatbot Development (WhatsApp, Web, CRM)',
            'Generative AI Integration & LLM Fine-Tuning',
            'Document Processing & Smart Data Analytics',
            'API & Webhook Automation (Zapier, n8n, Custom Nodes)'
        ],
        process: ['Workflow Audit', 'AI Architecture Blueprint', 'Custom AI Model Integration', 'Deploy & Monitor'],
        faqs: [
            { q: 'How can AI automation benefit small and medium businesses?', a: 'AI automation cuts operational costs by automating customer support, invoice processing, lead scoring, and routine data entry 24/7.' },
            { q: 'What platforms can your AI chatbots integrate with?', a: 'Our AI chatbots seamlessly connect with WhatsApp Business, web portals, Salesforce, HubSpot, and custom REST APIs.' }
        ]
    },
    'ui-ux-design': {
        title: 'UI/UX Design Services in Pune',
        metaTitle: 'UI/UX Design Services in Pune | Experience Design | PyrusMedia',
        metaDesc: 'Crafting intuitive user experiences. PyrusMedia provides UI/UX design services in Pune, user research, wireframing, and interactive design systems.',
        keywords: 'UI UX design services Pune, website design company Pune, mobile app UX design, wireframing and prototyping',
        summary: 'PyrusMedia provides top-tier UI/UX design services in Pune. We combine human-centered user research with stunning visual design to build products users love.',
        problems: ['High user drop-off during onboarding?', 'Confusing web/mobile app layout?', 'Inconsistent brand visual identity?'],
        included: [
            'User Research & Customer Journey Mapping',
            'Wireframing & Interactive Prototyping (Figma)',
            'Design Systems & UI Component Libraries',
            'Usability Testing & Design Audits'
        ],
        process: ['Empathize & Research', 'Wireframe & Prototype', 'Visual UI Design', 'Test & Deliver'],
        faqs: [
            { q: 'What design tools does your team use?', a: 'We utilize Figma, Adobe XD, and Illustrator to build reusable design components and interactive click-through prototypes.' }
        ]
    },
    'aeo-geo': {
        title: 'Generative Engine Optimization (GEO) & AEO Services',
        metaTitle: 'Generative Engine Optimization (GEO) & AEO Services Pune | PyrusMedia',
        metaDesc: 'Optimize for ChatGPT, Perplexity & Google SGE with leading GEO and AEO services in Pune. Be the recommended answer in AI search results.',
        keywords: 'generative engine optimization services, AEO services Pune, GEO services Pune, AI search optimization, answer engine optimization',
        summary: 'Future-proof your organic search presence with Answer Engine Optimization (AEO) and Generative Engine Optimization (GEO) services by PyrusMedia in Pune.',
        problems: ['Losing traffic to AI search answer overviews?', 'Brand missing from ChatGPT & Claude recommendation results?', 'Traditional SEO keywords not capturing AI user queries?'],
        included: [
            'AI Search Engine Visibility Audit (ChatGPT, Claude, Perplexity, Gemini)',
            'Entity Optimization & Structured Knowledge Graph Schema',
            'Generative Engine Optimization (GEO) Content Structuring',
            'Answer Engine Optimization (AEO) for Voice & Conversational Search',
            'Brand Citation & Digital Authority Building for LLMs'
        ],
        process: ['AI Search Audit', 'Entity & Schema Markup', 'Conversational Content Structuring', 'Tracking & Monitoring'],
        faqs: [
            { q: 'What is Generative Engine Optimization (GEO)?', a: 'GEO optimizes your brand content and structured data so conversational AI models (like ChatGPT and Google SGE) recommend your services.' },
            { q: 'Why is AEO critical for modern businesses?', a: 'More consumers ask AI direct questions instead of clicking blue links. AEO ensures your business is cited as the definitive answer.' }
        ]
    },
    'social-media-marketing': {
        title: 'Social Media Marketing Company in Pune',
        metaTitle: 'Social Media Marketing Company in Pune | PyrusMedia',
        metaDesc: 'Top social media marketing company in Pune. Creative social media management, Instagram marketing, Facebook ads, and viral content strategies.',
        keywords: 'social media marketing company Pune, social media management Pune, Instagram marketing agency Pune, Facebook marketing agency Pune',
        summary: 'Elevate your brand presence with a leading social media marketing company in Pune. PyrusMedia creates compelling graphics, short-form video reels, and paid campaigns.',
        problems: ['Low engagement on social channels?', 'Inconsistent posting schedule?', 'Ineffective ad targeting on Meta platforms?'],
        included: [
            'Social Media Management (Instagram, Facebook, LinkedIn, YouTube)',
            'Creative Content Strategy, Graphic Design & Video Reels',
            'Social Ads Campaign Management & Audience Targeting',
            'Community Management & Influencer Collaborations'
        ],
        process: ['Audience Analysis', 'Content Calendar', 'Production & Posting', 'Analytics Report'],
        faqs: [
            { q: 'Which social media platforms should my business focus on?', a: 'B2C brands thrive on Instagram & Facebook, while B2B companies get highest ROI from LinkedIn and targeted Google Search Ads.' }
        ]
    },
    'paid-ads': {
        title: 'Google Ads Agency & PPC Management in Pune',
        metaTitle: 'Google Ads Agency in Pune & PPC Management | PyrusMedia',
        metaDesc: 'Results-driven Google Ads agency in Pune. Expert PPC management, Meta ads agency campaigns, and targeted conversion funnels that maximize ad ROI.',
        keywords: 'Google Ads agency Pune, PPC agency Pune, Google Ads management Pune, Meta ads agency Pune, Facebook ads agency Pune',
        summary: 'PyrusMedia is a high-performance Google Ads agency and PPC management company in Pune specializing in instant customer generation.',
        problems: ['High cost per click with zero qualified sales leads?', 'Wasted budget on irrelevant search keywords?', 'Ad account suspensions or poor quality scores?'],
        included: [
            'Google Search, Display & Shopping Campaign Setup',
            'Meta (Facebook & Instagram) Retargeting Ads',
            'Conversion Tracking & High-Converting Landing Pages',
            'Continuous Bid Management & Negative Keyword Audits'
        ],
        process: ['Keyword & Competitor Research', 'Ad Copy & Landing Page Design', 'Campaign Launch', 'Weekly Optimization'],
        faqs: [
            { q: 'How quickly can Google Ads produce leads?', a: 'Google Ads can begin generating targeted impressions and customer inquiries within 24-48 hours of campaign launch.' }
        ]
    },
    'ecommerce': {
        title: 'Ecommerce Website Development Pune',
        metaTitle: 'Ecommerce Website Development Company in Pune | PyrusMedia',
        metaDesc: 'Premier ecommerce website development company in Pune. Custom online stores, Shopify development, and WooCommerce development optimized for sales.',
        keywords: 'ecommerce website development Pune, custom ecommerce development, Shopify development company Pune, WooCommerce development company Pune, online store development',
        summary: 'PyrusMedia is a leading ecommerce website development company in Pune crafting fast, secure, and sales-focused online shopping experiences.',
        problems: ['Complex checkout process causing abandoned carts?', 'Slow store speed on mobile devices?', 'Difficulty managing inventory and payment gateways?'],
        included: [
            'Custom E-commerce Development (React, Next.js, Node.js)',
            'Shopify & WooCommerce Custom Store Setup',
            'UPI, Credit Card, & International Payment Gateway Integration',
            'Mobile-Optimized Shopping Cart & Checkout Funnels'
        ],
        process: ['Store Architecture', 'UX/UI Store Design', 'Development & Integration', 'Security & Launch'],
        faqs: [
            { q: 'Do you build custom ecommerce platforms or Shopify stores?', a: 'We build both! We offer custom Next.js/React storefronts for custom features as well as Shopify & WooCommerce setups for fast launches.' }
        ]
    }
};

const ServiceDetail = () => {
    const { serviceId } = useParams();
    const data = serviceData[serviceId];

    if (!data) {
        return <Navigate to="/services" replace />;
    }

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": data.title,
        "provider": {
            "@type": "LocalBusiness",
            "name": "PyrusMedia",
            "address": {
                "@type": "PostalAddress",
                "addressLocality": "Pune",
                "addressRegion": "Maharashtra",
                "addressCountry": "IN"
            }
        },
        "description": data.summary,
        "serviceType": data.title
    };

    return (
        <>
            <Helmet>
                <title>{data.metaTitle || `${data.title} | PyrusMedia`}</title>
                <meta name="description" content={data.metaDesc || data.summary} />
                {data.keywords && <meta name="keywords" content={data.keywords} />}
                <script type="application/ld+json">
                    {JSON.stringify(schemaData)}
                </script>
            </Helmet>

            {/* Hero */}
            <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#bebd19]/10 rounded-full blur-[100px] pointer-events-none" />
                <div className="container mx-auto px-4 relative z-10">
                    <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">{data.title}</h1>
                    <p className="text-xl text-gray-500 max-w-3xl mx-auto mb-8 font-light">{data.summary}</p>
                    <Button to="/contact" className="px-8 py-3 text-lg">Request a Proposal</Button>
                </div>
            </section>

            {/* Problems & Solutions */}
            <Section className="bg-white">
                <div className="flex flex-col md:flex-row gap-12">
                    <div className="md:w-1/2">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Problems We Solve</h2>
                        <ul className="space-y-4">
                            {data.problems.map((prob, i) => (
                                <li key={i} className="flex items-center gap-3 text-gray-600 bg-gray-100 p-4 rounded-lg border border-gray-200 font-medium">
                                    <span className="w-2.5 h-2.5 bg-red-500 rounded-full shrink-0" />
                                    {prob}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:w-1/2">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">What's Included</h2>
                        <ul className="space-y-4">
                            {data.included.map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-gray-600">
                                    <CheckCircle2 className="text-[#bebd19] w-5 h-5 shrink-0" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>

            {/* Process */}
            <Section className="bg-gray-50">
                <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Our Process</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {data.process.map((step, i) => (
                        <div key={i} className="text-center p-6 bg-gray-100 rounded-xl border border-gray-200">
                            <div className="text-4xl font-bold text-gray-700 mb-2">0{i + 1}</div>
                            <div className="text-gray-900 font-semibold">{step}</div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Pricing Section (Conditional) */}
            {data.pricing && (
                <Section className="bg-white">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">Transparent Packages</h2>
                        <p className="text-gray-500 max-w-2xl mx-auto">Select a strategy package tailored to your growth goals.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto px-4">
                        {data.pricing.map((pkg, i) => (
                            <div
                                key={i}
                                className={`relative p-8 rounded-2xl border transition-all duration-300 flex flex-col ${pkg.isPopular ? 'bg-gray-50 border-[#bebd19] transform md:-translate-y-4 shadow-[0_0_30px_rgba(190,189,25,0.1)]' : 'bg-gray-100 border-gray-200 hover:border-gray-300'}`}
                            >
                                {pkg.isPopular && (
                                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-1 bg-[#bebd19] text-black text-xs font-bold uppercase tracking-wider rounded-full">
                                        Most Popular
                                    </div>
                                )}

                                <div className="mb-8">
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-6">{pkg.target}</p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-3xl font-bold text-[#bebd19]">{pkg.price}</span>
                                        <span className="text-gray-500 text-sm">{pkg.period}</span>
                                    </div>
                                </div>

                                <ul className="space-y-4 mb-8 flex-grow">
                                    {pkg.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-600">
                                            <CheckCircle2 className="text-[#bebd19] w-4 h-4 shrink-0 mt-0.5" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <Button to="/contact" className={`w-full justify-center ${pkg.isPopular ? 'bg-[#bebd19] text-black hover:bg-[#a3a215]' : 'bg-transparent border border-gray-700 hover:border-[#bebd19] hover:text-[#bebd19]'}`}>
                                    {pkg.buttonText}
                                </Button>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* FAQ */}
            <Section className="bg-white">
                <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Frequently Asked Questions</h2>
                <div className="max-w-3xl mx-auto grid gap-6">
                    {data.faqs.map((faq, i) => (
                        <div key={i} className="bg-gray-100 p-6 rounded-xl border border-gray-200">
                            <div className="flex items-start gap-3 mb-2">
                                <HelpCircle className="text-[#bebd19] w-5 h-5 mt-1 shrink-0" />
                                <h3 className="text-lg font-bold text-gray-900">{faq.q}</h3>
                            </div>
                            <p className="text-gray-600 pl-8 leading-relaxed">{faq.a}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* CTA */}
            <section className="bg-gray-50 border-t border-gray-200 text-center py-20 relative overflow-hidden">
                <div className="absolute inset-0 bg-[#bebd19]/5 blur-[100px] pointer-events-none" />
                <div className="container mx-auto px-4 relative z-10">
                    <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">Ready to scale with PyrusMedia?</h2>
                    <Button to="/contact" className="bg-[#bebd19] text-black hover:bg-[#a3a215] hover:scale-105 shadow-[0_0_20px_rgba(190,189,25,0.3)] px-10 py-4 text-lg">
                        Get a Free Consultation
                    </Button>
                </div>
            </section>
        </>
    );
};

export default ServiceDetail;

